import dayjs from 'dayjs';

/**
 * 라커 이용권 기간을 바꿀 때, 같은 자리의 다른 이용권을 어떻게 처리할지 계산한다.
 * 회원상세와 라커관리가 같은 결과를 내야 해서 한 곳에 둔다.
 *
 * 반환값
 *  - { blockedBy, blockedKind }  기간을 바꿀 수 없는 이용권과 겹친다. 저장하면 안 된다.
 *    blockedKind: 'expired' 지난 기록 | 'stopped' 중지 중
 *  - { entries, shiftedCount, shifted }  뒤 이용권을 밀어야 한다. 확인 후 배치로 저장한다.
 *    shifted 는 화면에 그대로 그릴 수 있게 변경 전/후를 담는다.
 *  - null                   겹치지 않는다. 그대로 저장하면 된다.
 *
 * 만료와 중지는 밀지 않는다.
 *  - 만료: 지난 기록이라 미래로 옮기면 되살아난다.
 *  - 중지: 남은 기간을 쓰지 않기로 하고 그만큼 종료일을 미뤄둔 상태다. 자리 사정으로 또 밀면
 *    회원이 신청한 중지 구간과 실제 이용 구간이 어긋난다. 기간은 기간 변경으로만 바꾼다.
 * 서버 겹침 검증은 둘 다 포함해서 보므로, 겹치면 미루지 말고 사유를 밝히며 막아야 한다.
 */
export const buildLockerShiftPlan = ({ spotPasses = [], passId, startDate, expireDate }) => {
  const today = dayjs().startOf('day');
  const isExpired = lp =>
    lp.expired ??
    (lp.status === 'INACTIVE' ||
      (lp.expireDate && dayjs(lp.expireDate).startOf('day').isBefore(today, 'day')));

  const all = spotPasses
    .filter(lp => lp.memberLockerPassId !== passId && lp.startDate && lp.expireDate)
    .map(lp => ({
      memberLockerPassId: lp.memberLockerPassId,
      productName: lp.productName,
      expired: isExpired(lp),
      stopped: lp.status === 'STOPPED',
      start: dayjs(lp.startDate).startOf('day'),
      end: dayjs(lp.expireDate).startOf('day'),
    }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const newStart = dayjs(startDate).startOf('day');
  const newEnd = dayjs(expireDate).startOf('day');

  const fixedHit = all.find(
    other =>
      (other.expired || other.stopped) &&
      !newStart.isAfter(other.end, 'day') &&
      !other.start.isAfter(newEnd, 'day')
  );
  if (fixedHit) {
    return {
      blockedBy: `${fixedHit.start.format('YYYY-MM-DD')} ~ ${fixedHit.end.format('YYYY-MM-DD')}`,
      blockedKind: fixedHit.stopped ? 'stopped' : 'expired',
    };
  }

  const others = all.filter(other => !other.expired && !other.stopped);
  if (others.length === 0) return null;

  const entries = [
    {
      memberLockerPassId: passId,
      startDate: newStart.format('YYYY-MM-DD'),
      expireDate: newEnd.format('YYYY-MM-DD'),
    },
  ];

  let cursor = newEnd.add(1, 'day');
  const shifted = [];

  others.forEach(other => {
    if (other.end.isBefore(newStart, 'day')) {
      // 앞에 끝난 이용권은 건드리지 않는다
      entries.push({
        memberLockerPassId: other.memberLockerPassId,
        startDate: other.start.format('YYYY-MM-DD'),
        expireDate: other.end.format('YYYY-MM-DD'),
      });
      return;
    }
    const span = other.end.diff(other.start, 'day') + 1;
    const needsShift = other.start.isBefore(cursor, 'day');
    const start = needsShift ? cursor : other.start;
    const end = start.add(span - 1, 'day');
    if (needsShift) {
      shifted.push({
        memberLockerPassId: other.memberLockerPassId,
        productName: other.productName,
        before: { startDate: other.start.toDate(), expireDate: other.end.toDate() },
        after: { startDate: start.toDate(), expireDate: end.toDate() },
      });
    }
    entries.push({
      memberLockerPassId: other.memberLockerPassId,
      startDate: start.format('YYYY-MM-DD'),
      expireDate: end.format('YYYY-MM-DD'),
    });
    cursor = end.add(1, 'day');
  });

  return { entries, shiftedCount: shifted.length, shifted };
};

export default buildLockerShiftPlan;
